// Read-only, shared date-only selection for record presentations. Provenance is
// evidence, not an inference from category, table, timestamp or formula equality.
import { getCurrentTermStartDate } from "./leaseDetailDatesHelpers.js";
const LABELS = {
  commencement: "Lease commencement", expiration: "Lease expiry", renewal_notice: "Renewal notice deadline",
  break_option: "Break option", break_notice: "Break notice", escalation: "Rent escalation", rent_review: "Rent review",
  deposit_burndown: "Deposit burndown", allowance_deadline: "Allowance deadline", allowance_warning: "Allowance warning",
  allowance_expiry: "Allowance expiry", cam_audit: "CAM audit", other: "Key date",
};
export function recordDateDay(value) {
  if (value instanceof Date) return Number.isFinite(value.getTime())
    ? `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}` : null;
  const day = String(value ?? "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day) || day.startsWith("9999")) return null;
  const d = new Date(`${day}T00:00:00`);
  return Number.isFinite(d.getTime()) && recordDateDay(d) === day ? day : null;
}
export function recordedDateLabel(day) {
  return day ? new Date(`${day}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";
}
export function criticalDateProvenance(row) {
  const evidence = row?.fieldSources?.dueDate ?? row?.field_sources?.due_date;
  const source = typeof evidence === "string" ? evidence : evidence?.source;
  if (source === "calculated") return "calculated";
  if (source && /spacebase|document|import/i.test(source)) {
    // The sync writer can overwrite due_date without replacing old provenance.
    // Require evidence tied to this value, or a confirmed import which is at
    // least as recent as the last row write, in addition to field attribution.
    const value = recordDateDay(evidence?.value ?? evidence?.date);
    const imported = Date.parse(row.lastCsvImportedAt ?? row.last_csv_imported_at);
    const updated = Date.parse(row.updatedAt ?? row.updated_at);
    if ((value && value === recordDateDay(row.dueDate ?? row.due_date)) ||
        (Number.isFinite(imported) && Number.isFinite(updated) && imported >= updated)) return "recorded";
  }
  return "ambiguous";
}

export function selectRecordDateEvents(lease, criticalDates, referenceDay = new Date()) {
  if (!lease) return { events: [], next: null, ambiguities: [] };
  const leaseId = lease.id ?? lease.leaseId;
  const today = recordDateDay(referenceDay);
  const rows = Array.isArray(criticalDates) ? criticalDates
    : Array.isArray(lease.criticalDates) ? lease.criticalDates : Array.isArray(lease.critical_dates) ? lease.critical_dates : [];
  const start = recordDateDay(getCurrentTermStartDate(rows, lease) ?? lease.startDate ?? lease.start_date ?? lease.leaseStart);
  const endValue = recordDateDay(lease.endDate ?? lease.end_date ?? lease.leaseEnd);
  const end = endValue && Number(endValue.slice(0, 4)) <= 2100 ? endValue : null;
  const rawMonths = lease.renewalNoticeMonths ?? lease.renewal_notice_months;
  const months = rawMonths !== null && rawMonths !== undefined && rawMonths !== "" ? Number(rawMonths) : null;
  const validTerm = Number.isInteger(months) && months >= 0;
  const explicitNotice = recordDateDay(lease.nextNoticeDeadline ?? lease.next_notice_deadline);
  const ambiguities = [];
  const qualified = [];
  const events = [];
  const add = (type, date, id, extra = {}) => {
    const day = recordDateDay(date);
    if (day) events.push({ id, leaseId, type, label: LABELS[type] ?? type, date: day, dueDate: day, provenance: "recorded", ...extra });
  };
  add("commencement", start, `${leaseId}-kd-start`);
  add("expiration", end, `${leaseId}-kd-expiry`);
  add("break_option", lease.breakDate ?? lease.break_date, `${leaseId}-kd-break`);
  let calculatedDay = null;
  let calculation = null;
  if (end && validTerm) {
    // Retain this application's established calendar setMonth convention.
    const d = new Date(`${end}T00:00:00`);
    d.setMonth(d.getMonth() - months);
    calculatedDay = recordDateDay(d);
    calculation = `Lease end ${recordedDateLabel(end)} minus ${months}-month notice term`;
  }
  if (explicitNotice && !validTerm) ambiguities.push({ leaseId, reason: "Explicit recorded notice conflicts with the missing notice term rule." });
  if (validTerm && explicitNotice) add("renewal_notice", explicitNotice, `${leaseId}-kd-notice`);

  for (const row of rows) {
    const type = row.type;
    if (type === "renewal_notice" && !validTerm) continue;
    const day = recordDateDay(row.dueDate ?? row.due_date);
    if (!day) {
      if (!row.completedAt && !row.completed_at && criticalDateProvenance(row) === "ambiguous")
        ambiguities.push({ leaseId, rowId: row.id, reason: "Critical date has no valid dated value or value-specific source evidence." });
      continue;
    }
    const provenance = criticalDateProvenance(row);
    if (provenance === "ambiguous" && day >= today && !row.completedAt && !row.completed_at) ambiguities.push({ leaseId, rowId: row.id, date: day, reason: "Critical date lacks value-specific document/import or calculation evidence." });
    if (provenance === "calculated" && (type !== "renewal_notice" || !calculation || day !== calculatedDay)) {
      ambiguities.push({ leaseId, rowId: row.id, reason: "Calculated row cannot be reconciled with the current lease term." });
      continue;
    }
    // Keep unproven rows in the existing history reader; never promote them to
    // recorded facts or deduplicate them against an authoritative field.
    if (provenance === "ambiguous") {
      qualified.push({ id: `${leaseId}-kd-cd-${row.id}`, leaseId, type,
        label: row.categoryRaw || LABELS[type] || type, date: day, dueDate: day,
        provenance, completedAt: row.completedAt ?? row.completed_at, sourceRow: row,
        disclosure: "Source unverified: this date lacks value-specific document/import or calculation evidence." });
      continue;
    }
    add(type, day, `${leaseId}-kd-cd-${row.id}`, {
      provenance, completedAt: row.completedAt ?? row.completed_at,
      label: row.categoryRaw || LABELS[type] || type,
      disclosure: provenance === "calculated" ? calculation : null,
      sourceRow: row,
    });
  }
  if (validTerm && !explicitNotice && calculatedDay &&
      !events.some(e => e.type === "renewal_notice" && e.date === calculatedDay && e.completedAt) &&
      !events.some(e => e.type === "renewal_notice" && e.provenance === "recorded" && !e.completedAt)) {
    add("renewal_notice", calculatedDay, `${leaseId}-kd-notice-derived`, { provenance: "calculated", disclosure: calculation });
  }
  // Only explicit evidence permits equivalent-event collapse. Recorded wins.
  events.sort((a, b) => a.date.localeCompare(b.date) || (a.provenance === b.provenance ? a.type.localeCompare(b.type) : a.provenance === "recorded" ? -1 : 1));
  const seen = new Set();
  const unique = events.filter(e => {
    // A matching generic type/date does not prove that two sourced obligations
    // are the same event (e.g. rent escalation and a LOC adjustment).
    const termPoint = ["commencement", "expiration", "renewal_notice", "break"].includes(e.type);
    const identity = !termPoint && e.sourceRow ? e.sourceRow.id ?? e.sourceRow.categoryRaw ?? e.id : "";
    const key = `${e.leaseId}|${e.type}|${e.date}|${Boolean(e.completedAt)}|${identity}`;
    if (seen.has(key)) return false;
    seen.add(key); return true;
  });
  const recordedNotice = unique.some(e => e.type === "renewal_notice" && e.provenance === "recorded" &&
    !e.completedAt && (!start || e.date >= start));
  const upcoming = unique.filter(e => !e.completedAt && e.date >= today && (!start || e.date >= start) &&
    !(recordedNotice && e.type === "renewal_notice" && e.provenance === "calculated"));
  // Keep uncertain rows out of the authoritative list and its deduplication.
  // Undated source-review issues must not erase a dated candidate.
  const rank = { recorded: 0, calculated: 1, ambiguous: 2 };
  const eligibleQualified = qualified.filter(e => !start || e.date >= start);
  const displayEvents = [...unique, ...eligibleQualified].sort((a, b) => a.date.localeCompare(b.date) ||
    rank[a.provenance] - rank[b.provenance] || a.type.localeCompare(b.type));
  const candidates = [...upcoming, ...eligibleQualified.filter(e => !e.completedAt && e.date >= today)]
    .sort((a, b) => a.date.localeCompare(b.date));
  const next = candidates[0] ?? null;
  const blocked = ambiguities.length > 0;
  return { events: unique, displayEvents, next, ambiguities, blocked };
}
